// Implementation of cache of property policy data indexed by ObjectId

import Debug from 'debug';
import { ObjectType, PropertyType } from '@pvw/plmvisweb';
import { fromPsId } from '@atlas/atlas-convert-ids';
import { get, setWith } from 'lodash';
import { isDerivedClass, isSdkClassName } from '@atlas/api-utils';

const debug = Debug('atlasPropCache.js');

// For transient objects, atlasId is a number, for tagged objects, it is an array of strings
/** @typedef {Array} AtlasId */
/** @typedef {{ObjectId:AtlasId, Class:string}} ISPLMObject */

/** @type {import('@pvw/plmvisweb').Viewer} */
let _viewer;
let _sendApi;
/** @type {Record<string, { Class: string, SetPropertyPolicy: { className: string, properties: string[] } }>} */
let _policies = {};
let _propCache = {};
let _onDemandData;
let _requestCount = {};

/**
 *
 * @param event
 */
export function _listener(event) {
    cacheByIds(
        event.candidateIds.flatMap((psId) => {
            try {
                return [fromPsId(_viewer, psId, true)];
            } catch {
                debug('invalid psId');
                return [];
            }
        })
    );
}

/**
 *
 * @param changes
 */
function onGeometryChanged(changes) {
    if (changes.Body) {
        let cleared = false;
        for (const [changeType, psIds] of Object.entries(changes.Body)) {
            if (changeType === 'updated') {
                for (let psId of psIds) {
                    // only clear if it is not marked as a preview entity
                    let props = _viewer.properties.getProperties(psId, false, PropertyType.PlmProperties);
                    if (!props || !props.properties || !props.properties.PreviewEntity) {
                        clear();
                        cleared = true;
                        break;
                    }
                }
                if (cleared) break;
            }
        }
    }
}

/**
 *
 * @param viewer
 * @param sendApi
 */
export function initialize(viewer, sendApi) {
    if (viewer !== _viewer) {
        clear();
        if (_viewer) {
            _viewer.unregisterGeometryChangedEvent(onGeometryChanged);
        }
        _viewer = viewer;
        if (_viewer) {
            _viewer.registerGeometryChangedEvent([ObjectType.BODY], onGeometryChanged);
        }
    }

    _sendApi = sendApi;
}

/**
 *
 * @param onDemandData
 */
export function initializeOnDemandData(onDemandData) {
    if (_onDemandData) {
        _onDemandData.removeListener(_listener);
    }
    _onDemandData = onDemandData;
    if (_onDemandData) {
        _onDemandData.addListener(_listener);
    }
}

/**
 *
 * @param objOrId
 * @returns {AtlasId}
 */
function normalizeId(objOrId) {
    return objOrId.ObjectId || objOrId;
}

// for internal use and testing only
export const _impl = {
    setCache: (cache) => (_propCache = cache),
    getCache: () => _propCache,

    // using setWith rather than set, so that we can
    // use a "Customizer" to ensure that an object, rather
    // than an array will be created
    updateCache: (key, value) => setWith(_propCache, key, value, Object),
    getFromCache: (key) => get(_propCache, normalizeId(key)),
    traverseCache(callback, target = _propCache) {
        for (const [key, value] of Object.entries(target)) {
            if (value.Class) {
                callback(target, key, value);
            } else if (typeof value === 'object') {
                this.traverseCache(callback, value);
            }
        }
    },
    clearPolicies: () => (_policies = {}),
    getPolicies: () => _policies
};

/**
 *
 */
export function clear() {
    _propCache = {};
    _requestCount = {};
}

/**
 *
 * @param {string} className
 * @param {string[]} propsDesired
 */
export function register(className, propsDesired) {
    if (!isSdkClassName(className)) {
        throw new Error('Invalid Atlas SDK class name');
    }
    let currentProps = _policies[className] ? _policies[className].SetPropertyPolicy.properties : [];
    let newProps = [...new Set([...currentProps, ...propsDesired])];

    if (newProps.length !== currentProps.length) {
        _policies[className] = {
            Class: 'AtlasSession',
            SetPropertyPolicy: {
                className,
                properties: newProps
            }
        };

        // different props registered - go invalidate all entries of this class
        if (currentProps.length) {
            _impl.traverseCache((container, key, value) => {
                if (isDerivedClass(className, value.Class)) {
                    _requestCount = {}; // request counts could be invalid if we delete a cache entry
                    delete container[key];
                }
            });
        }
    }
}

/**
 * Unregisters the specified properties for the specified class. If no properties are left, the class will be unregistered.
 *
 * @param {string} className
 * @param {string[]} props
 */
export function unregisterProperties(className, props) {
    let currentProps = _policies[className] ? _policies[className].SetPropertyPolicy.properties : [];
    let newProps = currentProps.filter((prop) => !props.includes(prop));
    if (newProps.length === 0) {
        delete _policies[className];
    } else {
        _policies[className] = {
            Class: 'AtlasSession',
            SetPropertyPolicy: {
                className,
                properties: newProps
            }
        };
    }
}

/**
 *
 * @param {Array} api
 * @returns {Promise}
 */
async function _getFromAtlas(api) {
    return _sendApi?.(
        {
            API: [...Object.values(_policies), ...api],
            ApiDesciption: 'objectPropCache'
        },
        {
            preprocessError: (err) => {
                debug('Error retrieving object props', err);
                return false; // suppress errors
            }
        }
    );
}

/**
 *
 * @param result
 */
function _cacheAtlasResult(result) {
    if (!result || !result.PROPERTY_POLICY_RESULT) {
        debug('No results to process');
        return;
    }
    for (let res of result.PROPERTY_POLICY_RESULT) {
        if (res.Class && res.ObjectId) {
            _impl.updateCache(res.ObjectId, res);
        } else {
            debug('Prop result without Class and/or ObjectId');
        }
    }
}

/**
 *
 * @param {Array<AtlasId|ISPLMObject>} ids
 */
export async function cacheByIds(ids) {
    let notCached = ids.filter((id) => !_impl.getFromCache(id));
    if (notCached.length) {
        debug('Requesting data for:', notCached);
        let result = await _getFromAtlas(
            notCached.map((id) => ({
                ObjectId: normalizeId(id),
                Class: 'SPLM.NXObject'
            }))
        );
        _cacheAtlasResult(result);
    }
}

/**
 * This function will cache the input psIds only after this function has been called a number of
 * times (determined by the count parameter).  This function is intended for use with selection and
 * on-demand-data.  By design on-demand-data won't request data for smaller entities in the current
 * view.  But its algorithm is just guessing, and it's possible it will exclude entities that the user
 * might still want to select.  This function will allow subsequent requests for specific entities.  It
 * takes a 'count' paramter to numb the behavior.  If the user just happens to roll over a small entity
 * we don't need to request it.  If the user is trying to select the object, we'll receive multiple
 * mouse events over the entity.  The default of 10 is arbitrary, based on what seems to work well in
 * practice.
 *
 * @function cacheByPsIdsDelayedCount
 * @param {Array}   psIds - psIds to cache when appropriate
 * @param {boolean}  [partOnly=true] - true to cache for prototype part; false for assembly
 * @returns {Promise}
 */
export async function cacheByPsIdsDelayedCount(psIds, partOnly = true) {
    //here I can request this edge if it gets pinged enough.  I don't want to do it the first time
    // I get a request because I want to avoid random tiny objects the mouse might be over
    let count = 10;
    let cachePsIds = [];
    for (let psId of psIds) {
        const curCount = _requestCount[psId] || 0;
        _requestCount[psId] = curCount + 1;
        // exact comparison below.  Any less, we haven't gotten enough hits.  Any more, a request has already been issued
        // but we haven't gotten a response
        if (curCount === count - 1) {
            cachePsIds.push(psId);
        }
    }
    if (cachePsIds.length > 0) {
        //console.log("Request data for ", cachePsIds);
        // I don't know how messy returning this promise will be. I guess the other option is to
        // swallow it but we're returning a promise all the time anyway so it seems reasonable to return it.
        return cacheByPsIds(cachePsIds, partOnly);
    }
}

/**
 * Caches the input array of psIds
 *
 * @param {string[]} psIds - array of psIds to cache
 * @param {boolean} [partOnly=false] - true to cache the prototype part only; false for the assembly
 * @returns {Promise} atlas result cached
 */
export async function cacheByPsIds(psIds, partOnly = false) {
    return cacheByIds(psIds.map((psId) => fromPsId(_viewer, psId, partOnly)));
}

/**
 *
 * @param {Array<AtlasId|ISPLMObject>} atlasIds
 * @returns {any[]}
 */
export function getCachedByIds(atlasIds) {
    return atlasIds.map((id) => _impl.getFromCache(id) || { ObjectId: normalizeId(id) });
}

/**
 *
 * @param {string[]} psIds
 * @param {boolean} [partOnly=false]
 * @returns {any[]}
 */
export function getCachedByPsIds(psIds, partOnly = false) {
    return getCachedByIds(psIds.map((psId) => fromPsId(_viewer, psId, partOnly)));
}

/**
 * Gets cached data for the specified inputs if it is there. If not cached, placeholder objects will be returned in the array.
 *
 * @param {Array<AtlasId|ISPLMObject>} atlasIds
 * @returns {Promise<any[]>}
 */
export async function getByIds(atlasIds) {
    await cacheByIds(atlasIds);
    return getCachedByIds(atlasIds);
}

/**
 * Gets data for the specified IDs. If not cached, it will be retrieved on demand.
 *
 * @param {string[]} psIds
 * @param {boolean} [partOnly=false]
 * @returns {Promise<any[]>}
 */
export async function getByPsIds(psIds, partOnly = false) {
    return getByIds(psIds.map((psId) => fromPsId(_viewer, psId, partOnly)));
}

/**
 * The specified APIs will be called to populate the cache from the objects returned via property policy.
 *
 * @param {Array} api
 * @returns {Promise}
 */
export async function cacheFromApi(api) {
    let result = await _getFromAtlas(api);
    _cacheAtlasResult(result);
    return result;
}

export default {
    initialize,
    initializeOnDemandData,
    register,
    unregisterProperties,
    clear,
    getCachedByPsIds,
    getCachedByIds,
    getByPsIds,
    getByIds,
    cacheByIds,
    cacheByPsIds,
    cacheByPsIdsDelayedCount,
    cacheFromApi
};
